import type { StudentAssignment, BibleLocation, Student, WordSelection } from '@skjv/common';
import { isWordLevel } from '@skjv/common';
import { LocationDropTarget } from './location-drop-target';
import { WordPickEditor } from './word-pick-editor';

interface AssignmentCardProps {
  student: Student;
  assignment?: StudentAssignment;
  onQuestionChange: (question: string) => void;
  onStartLocationChange: (location: BibleLocation | null) => void;
  onEndLocationChange: (location: BibleLocation | null) => void;
  onWordPick: (selection: WordSelection, fragment: string) => void;
}

/**
 * Per-student assignment card: name, level chips, reading drop targets,
 * the word picker for word-mode readers, and the review question.
 */
export function AssignmentCard({
  student,
  assignment,
  onQuestionChange,
  onStartLocationChange,
  onEndLocationChange,
  onWordPick,
}: AssignmentCardProps) {
  const showWordPick = isWordLevel(student.readingLevel) && assignment?.startingLocation;

  return (
    <div
      data-testid={`assignment-card-${student.id}`}
      className="rounded-lg border border-stone-200/30 bg-white/50 p-3 dark:border-stone-700/20 dark:bg-stone-900/30"
    >
      {/* Student header — levels live in the student setup now, no chips
          (Brian 2026-09-04) */}
      <div className="mb-3 text-base font-medium text-stone-800 dark:text-stone-200">
        {student.name}
      </div>

      {/* Reading range */}
      <div className="mb-3 grid grid-cols-2 gap-2">
        <LocationDropTarget
          label="Read from"
          location={assignment?.startingLocation}
          onDrop={onStartLocationChange}
          placeholder="Drop verse"
        />
        <LocationDropTarget
          label="Read to"
          location={assignment?.endingLocation}
          onDrop={onEndLocationChange}
          placeholder="(same verse)"
        />
      </div>

      {/* Word pick — early readers choose word(s) within the dropped verse */}
      {showWordPick && assignment?.startingLocation && (
        <WordPickEditor
          startingLocation={assignment.startingLocation}
          endingLocation={assignment.endingLocation}
          readingLevel={student.readingLevel}
          writingLevel={student.writingLevel}
          wordSelection={assignment.wordSelection}
          onPick={onWordPick}
        />
      )}

      {/* Review question */}
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
          Review Question
        </label>
        <textarea
          value={assignment?.reviewQuestion || ''}
          onChange={(e) => onQuestionChange(e.target.value)}
          placeholder="Ask a question tailored to this student's level..."
          rows={3}
          className="w-full resize-none rounded-lg border border-stone-300/50 bg-white/80 px-2.5 py-2 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 dark:border-stone-600/50 dark:bg-stone-800/60 dark:text-stone-200 dark:placeholder-stone-500"
        />
      </div>
    </div>
  );
}
